import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePreferences } from '../contexts/PreferencesContext';
import { safeStorage } from '../utils/safeStorage';
import { logger } from '../utils/logger';

const SHIFT_NAV_MAP = {
  I: '/inbox',
  T: '/',
  K: '/calls',
  R: '/reading',
  M: '/payments',
  A: '/analytics',
  C: '/calendar',
  O: '/commitments', // Obligations
  L: '/templates',   // Library
};

function toggleCinemaMode() {
  document.documentElement.toggleAttribute('data-cinema');
  try {
    const current = document.documentElement.hasAttribute('data-cinema');
    safeStorage.setString('polkdesq_cinema_mode', current ? '1' : '0');
  } catch (err) {
    logger.warn('Cinema-mode preference not persisted (storage disabled):', err?.message);
  }
}

/**
 * Global keyboard shortcuts for the app shell.
 * - Cmd+K → PolkTaskHub (command bar)
 * - Cmd+/ (or Ctrl+/) → global search overlay
 * - Cmd+N → Quick Add modal (universal capture)
 * - Cmd+Shift+D → Quick Add modal in Brain Dump mode (chained capture)
 * - Cmd+Shift+N → toggle notes
 * - Cmd+Shift+{key} → navigate views
 * - ? → open shortcuts cheat sheet
 * - Escape → close notes
 *
 * Uses the "latest ref" pattern: the keydown listener is attached exactly
 * once, and always reads the freshest handlers/preferences from a ref —
 * no re-subscribe churn, no stale closures.
 */
export function useAppShortcuts({ onToggleNotes, onOpenShortcuts, onOpenSearch, onOpenCommandBar, onOpenQuickAdd, onOpenBrainDump }) {
  const navigate = useNavigate();
  const { preferences } = usePreferences();

  const latestRef = useRef({});
  latestRef.current = {
    onToggleNotes, onOpenShortcuts, onOpenSearch, onOpenCommandBar, onOpenQuickAdd, onOpenBrainDump,
    navigate, commandBarEnabled: preferences.commandBarEnabled,
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const h = latestRef.current;
      const tag = document.activeElement?.tagName;
      const isEditable = document.activeElement?.isContentEditable;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || isEditable;

      // Cmd/Ctrl + \ → Toggle Cinema mode (hide sidebar for deep work)
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        toggleCinemaMode();
        return;
      }

      // F11 → Toggle Cinema mode (when not in form input)
      if (!isInput && e.key === 'F11') {
        e.preventDefault();
        toggleCinemaMode();
        return;
      }

      // Cmd+K → CommandBar modal (falls back to /hub navigate if handler not provided)
      if (h.commandBarEnabled && (e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (h.onOpenCommandBar) {
          h.onOpenCommandBar();
        } else {
          h.navigate('/hub');
        }
        return;
      }

      // Cmd+/ → global search
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        h.onOpenSearch?.();
        return;
      }

      // Cmd+Shift+D → Brain Dump mode (chained capture). Mnemonic: D
      // for "dump". Distinct from Cmd+N so users can choose between
      // single-shot vs rapid-fire from the keyboard.
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toUpperCase() === 'D') {
        e.preventDefault();
        h.onOpenBrainDump?.();
        return;
      }

      // Cmd+Shift+N → toggle notes panel
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toUpperCase() === 'N') {
        e.preventDefault();
        h.onToggleNotes?.();
        return;
      }

      // Cmd+N → Universal Quick Add modal. Browsers reserve Cmd+N
      // for "new window" but only when fired on the document root —
      // because we preventDefault inside an active app surface,
      // most browsers honor the override. (Safari mac still owns
      // it as a hard browser shortcut; users on Safari should use
      // the topbar `+` button or Cmd+N from inside a focused input.)
      if (!e.shiftKey && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
        // Only intercept when NOT in an input — typing Cmd+N inside
        // a text field is a legit "open new window" intent and we
        // shouldn't steal it. Inside the app shell, intercept.
        if (!isInput) {
          e.preventDefault();
          h.onOpenQuickAdd?.();
          return;
        }
      }

      // Cmd+Shift+{letter} → navigate views
      if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
        const path = SHIFT_NAV_MAP[e.key.toUpperCase()];
        if (path) {
          e.preventDefault();
          h.navigate(path);
        }
        return;
      }

      // "?" → open shortcuts cheat sheet (ignore while typing)
      if (!isInput && !e.metaKey && !e.ctrlKey && !e.altKey && e.key === '?') {
        e.preventDefault();
        h.onOpenShortcuts?.();
        return;
      }

      // Escape → close notes
      if (e.key === 'Escape') {
        h.onToggleNotes?.(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}
